import { useEffect, useState } from 'react';

/**
 * Hauteur, en pixels, dont le clavier logiciel recouvre le bas de la page.
 *
 * Pourquoi ce hook existe : sur iOS, l'ouverture du clavier ne rétrécit PAS le
 * viewport de mise en page. Un `flex h-full flex-col` place donc sa barre de
 * saisie sous le clavier — le geste n°1 de l'app (répondre à une cliente)
 * casserait au premier essai. Seul `visualViewport` voit le clavier ; même
 * `100dvh` ne le voit pas.
 *
 * Deux pièges, tous deux vérifiés, tous deux évités ici :
 *
 * 1. **Ne jamais publier `visualViewport.height` comme une hauteur.** Elle vaut
 *    *hauteur de mise en page ÷ échelle* — prouvé par le test WebKit
 *    `rtl-zoomed-rects-expected.txt`, où 785 devient 392,5 à 2×. La publier
 *    replierait l'app pendant un pincement. On publie donc un DÉCALAGE, et on
 *    rend 0 dès que la page est zoomée : suivre le clavier pendant un pincement
 *    n'a pas de sens, et une valeur fausse est pire qu'une valeur absente.
 *
 * 2. **Ne jamais composer ce décalage avec `position: fixed`.** Le moteur
 *    applique déjà la compensation aux éléments fixes : l'ajouter la
 *    doublerait. Ce décalage s'applique au flux, jamais à une superposition —
 *    c'est pourquoi `BottomSheet` ne le consomme pas.
 */
export function useKeyboardInset(): number {
    const [inset, setInset] = useState(0);

    useEffect(() => {
        const vv = window.visualViewport;
        if (!vv) return;

        const mesurer = () => {
            // Au-delà d'un pincement franc, on ne prétend plus rien savoir.
            if (vv.scale > 1.01) {
                setInset(0);

                return;
            }

            const recouvrement = window.innerHeight - (vv.height + vv.offsetTop);
            setInset(recouvrement > 0 ? Math.round(recouvrement) : 0);
        };

        mesurer();
        vv.addEventListener('resize', mesurer);
        vv.addEventListener('scroll', mesurer);

        return () => {
            vv.removeEventListener('resize', mesurer);
            vv.removeEventListener('scroll', mesurer);
        };
    }, []);

    return inset;
}
